import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";
import SectionCard from "@/components/shared/SectionCard";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { TierBadge } from "@/components/shared/TierBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/admin/verifications/")({
  component: AdminVerifications,
});

type Student = {
  id: number;
  name: string;
  email: string;
  membership_tier: string;
  verification_status: string;
  university: string;
  created_at: string;
};

function verificationVariant(status: string) {
  if (status === "verified") return "success" as const;
  if (status === "pending_review") return "warning" as const;
  return "destructive" as const;
}

function AdminVerifications() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("pending_review");

  const { data: students = [], isLoading } = useQuery({
    queryKey: ["admin-students"],
    queryFn: async () => {
      const res = await apiClient.get("/admin/students");
      return res.data.data as Student[];
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async ({ id, status, reason }: { id: string; status: string; reason?: string }) => {
      await apiClient.post(`/admin/students/${id}/verify`, { status, reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-students"] });
      toast.success("Student verification updated");
    },
    onError: () => {
      toast.error("Failed to update verification");
    },
  });

  const filtered =
    filter === "all" ? (students ?? []) : (students ?? []).filter((s) => s.verification_status === filter);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Student verifications"
        subtitle="Review and verify student identities to unlock verified tier access."
      />

      <SectionCard
        title="Students"
        description={isLoading ? undefined : `${filtered.length} shown`}
        noPadding
        actions={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[200px]" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All students</SelectItem>
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      >
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon={<ShieldCheck />}
            title="No students found"
            description="No students found with the selected filter."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Student</TableHead>
                <TableHead>University</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((student) => (
                <TableRow key={student.id} className="h-11 [&>td]:py-1.5">
                  <TableCell className="min-w-[200px] pl-4 md:pl-5">
                    <p className="font-medium text-foreground">{student.name}</p>
                    <p className="text-caption text-muted-foreground">{student.email}</p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-body">
                    {student.university || "No university"}
                  </TableCell>
                  <TableCell>
                    <TierBadge
                      tier={student.membership_tier as any}
                      verificationStatus={student.verification_status}
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant={verificationVariant(student.verification_status)} dot>
                      {student.verification_status.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="pr-4 md:pr-5">
                    <div className="flex justify-end gap-1.5">
                      {student.verification_status !== "verified" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            verifyMutation.mutate({ id: String(student.id), status: "verified" })
                          }
                        >
                          <Check />
                          Approve
                        </Button>
                      )}
                      {student.verification_status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="destructive-outline"
                          onClick={() =>
                            verifyMutation.mutate({
                              id: String(student.id),
                              status: "rejected",
                              reason: "Documents unclear",
                            })
                          }
                        >
                          <X />
                          Reject
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
